import React, { useState } from 'react';

// ─── HOUSE INFO VIEW ─────────────────────────────────────────────────────────
// Shared by SharedView (the caregiver surface) and the owner's Settings → House.
// Renders all household data from houseData sub-collections.
// Sections:
//   🚨 EMERGENCY INFO (top, always visible)
//      — Location/address, Emergency services, Contacts (parents first)
//   👧 Kids — per-child medical, allergies, meds, bedtime, behaviour
//   🐾 Pets — per-pet care, meds, feeding, vet info
//   🏠 House — WiFi, alarm, safety items, rules, off-limits areas
//   📋 Notes — general sitter notes

// fixedSection: pin one section and hide the nav (used by the caregiver tabs).
// hideInsurance: suppress insurance provider/member id (token-link viewers).
export default function HouseInfoView({ familyData, fixedSection = null, hideInsurance = false }) {
  const [openSection, setSection] = useState('emergency');
  const section = fixedSection || openSection;

  const houseData = familyData?.houseData || {};
  const location  = houseData.location   || {};
  // Owner-only private/security (alarm/gate codes) merges over safetyInfo —
  // non-owners never load it, so those rows simply don't render for them.
  const safety    = { ...(houseData.safetyInfo || {}), ...(houseData.security || {}) };
  const services  = houseData.emergencyServices || {};
  const contacts  = houseData.contacts   || [];
  const pets      = houseData.pets       || [];
  const children  = familyData?.children || [];
  const insuranceMap = houseData.insurance || {};
  const notes     = familyData?.houseData?.notes || familyData?.config?.houseInfo?.houseRules || '';

  const SECTIONS = [
    { id: 'emergency', label: '🚨 Emergency' },
    { id: 'kids',      label: '🌱 Kids' },
    ...(pets.length > 0 ? [{ id: 'pets', label: '🐾 Pets' }] : []),
    { id: 'house',     label: '🪵 House' },
    ...(notes ? [{ id: 'notes', label: '📋 Notes' }] : []),
  ];

  return (
    <div className="house-info-view">
      {/* Section nav — hidden when pinned to one section */}
      {!fixedSection && (
        <div className="hiv-nav">
          {SECTIONS.map(s => (
            <button key={s.id}
              className={`hiv-nav__btn ${section === s.id ? 'hiv-nav__btn--active' : ''}`}
              onClick={() => setSection(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {section === 'emergency' && <EmergencySection location={location} services={services} contacts={contacts} />}
      {section === 'kids'      && <KidsSection children={children} hideInsurance={hideInsurance} insuranceMap={insuranceMap} />}
      {section === 'pets'      && <PetsSection pets={pets} />}
      {section === 'house'     && <HouseSection safety={safety} location={location} norms={houseData.norms || {}} />}
      {section === 'notes'     && <NotesSection notes={notes} />}
    </div>
  );
}

// ─── EMERGENCY SECTION ───────────────────────────────────────────────────────

function EmergencySection({ location, services, contacts }) {
  const addr = [location.street_line_1, location.street_line_2, location.city, location.state, location.zip_code]
    .filter(Boolean).join(', ');

  const parents  = contacts.filter(c => ['parent','guardian'].includes(c.contact_role));
  const backups  = contacts.filter(c => !['parent','guardian'].includes(c.contact_role));

  // Sort emergency services by priority
  const SERVICE_ORDER = ['police','fire','ambulance','hospital','poison_control','crisis_line','vet_emergency','vet_poison'];
  const serviceEntries = Object.entries(services)
    .filter(([, v]) => v && v.primary_phone)
    .sort(([a],[b]) => SERVICE_ORDER.indexOf(a) - SERVICE_ORDER.indexOf(b));

  const SERVICE_ICONS = {
    police:'🚔', fire:'🔥', ambulance:'🚑', hospital:'🏥',
    poison_control:'☠️', crisis_line:'🫂', vet_emergency:'🐾', vet_poison:'🐾',
  };
  const SERVICE_LABELS = {
    police:'Police', fire:'Fire', ambulance:'Ambulance', hospital:'Hospital',
    poison_control:'Poison Control', crisis_line:'Crisis Line',
    vet_emergency:'Emergency Vet', vet_poison:'Vet Poison Control',
  };

  return (
    <div className="hiv-section">

      {/* Emergency banner */}
      <div className="hiv-emergency-banner">
        <span className="hiv-emergency-banner__icon">🚨</span>
        <div>
          <div className="hiv-emergency-banner__title">Emergency — call 911 first</div>
          {addr && <div className="hiv-emergency-banner__addr">{addr}</div>}
          {location.cross_street && <div className="hiv-emergency-banner__cross">Near: {location.cross_street}</div>}
        </div>
        <a href="tel:911" className="hiv-call-btn hiv-call-btn--red">Call 911</a>
      </div>

      {/* Fire meeting point */}
      {location.fire_meeting_point && (
        <InfoRow icon="🔥" label="Fire meeting point" value={location.fire_meeting_point} />
      )}

      {/* Parents / guardians first */}
      {parents.length > 0 && (
        <div className="hiv-group">
          <div className="hiv-group__title">Who to call</div>
          {parents.map(c => <ContactCard key={c.contact_id} contact={c} priority />)}
        </div>
      )}

      {/* Emergency services */}
      {serviceEntries.length > 0 && (
        <div className="hiv-group">
          <div className="hiv-group__title">Emergency services</div>
          {serviceEntries.map(([type, svc]) => (
            <div key={type} className="hiv-service-row">
              <span className="hiv-service-row__icon">{SERVICE_ICONS[type] || '📞'}</span>
              <div className="hiv-service-row__info">
                <span className="hiv-service-row__name">{svc.service_name || SERVICE_LABELS[type]}</span>
                {svc.address && <span className="hiv-service-row__addr">{svc.address}</span>}
                {svc.notes   && <span className="hiv-service-row__note">{svc.notes}</span>}
              </div>
              <a href={`tel:${svc.primary_phone}`} className="hiv-call-btn">{svc.primary_phone}</a>
            </div>
          ))}
        </div>
      )}

      {/* Backup contacts */}
      {backups.length > 0 && (
        <div className="hiv-group">
          <div className="hiv-group__title">Backup contacts</div>
          {backups.map(c => <ContactCard key={c.contact_id} contact={c} />)}
        </div>
      )}

      {/* Fallback if nothing is set */}
      {!addr && parents.length === 0 && serviceEntries.length === 0 && (
        <p className="hiv-empty">No emergency info added yet. Ask the family to fill in Settings → House &amp; Emergency Info.</p>
      )}
    </div>
  );
}

function ContactCard({ contact, priority }) {
  return (
    <div className={`hiv-contact-card ${priority ? 'hiv-contact-card--priority' : ''}`}>
      <div className="hiv-contact-card__header">
        <span className="hiv-contact-card__name">{contact.full_name}</span>
        <span className="hiv-contact-card__role">{contact.relationship_to_child || contact.contact_role}</span>
      </div>
      {contact.destination_name && (
        <div className="hiv-contact-card__dest">
          At: {contact.destination_name}
          {contact.destination_address && ` — ${contact.destination_address}`}
        </div>
      )}
      {contact.expected_return_time && (
        <div className="hiv-contact-card__return">
          Back: {new Date(contact.expected_return_time).toLocaleString('en-US',{weekday:'short',hour:'numeric',minute:'2-digit'})}
        </div>
      )}
      {contact.notes && <div className="hiv-contact-card__note">{contact.notes}</div>}
      <div className="hiv-contact-card__phones">
        {contact.mobile_phone && (
          <a href={`tel:${contact.mobile_phone}`} className="hiv-call-btn hiv-call-btn--forest">
            📞 {contact.mobile_phone}
          </a>
        )}
        {contact.secondary_phone && (
          <a href={`tel:${contact.secondary_phone}`} className="hiv-call-btn">
            {contact.secondary_phone}
          </a>
        )}
      </div>
    </div>
  );
}

// ─── KIDS SECTION ─────────────────────────────────────────────────────────────

function KidsSection({ children, hideInsurance = false, insuranceMap = {} }) {
  const [open, setOpen] = useState(children[0]?.id || null);

  if (!children.length) return <p className="hiv-empty">No children added yet.</p>;

  return (
    <div className="hiv-section">
      {/* Kid picker */}
      {children.length > 1 && (
        <div className="hiv-kid-tabs">
          {children.map(c => (
            <button key={c.id}
              className={`hiv-kid-tab ${open === c.id ? 'hiv-kid-tab--active' : ''}`}
              onClick={() => setOpen(c.id)}>
              {c.preferred_name || c.firstName}
            </button>
          ))}
        </div>
      )}

      {children.filter(c => c.id === open || children.length === 1).map(child => (
        <KidDetail key={child.id} child={child} hideInsurance={hideInsurance} insurance={insuranceMap[child.id]} />
      ))}
    </div>
  );
}

function KidDetail({ child, hideInsurance = false, insurance = null }) {
  // Insurance ids live in owner-only private/insurance post-migration;
  // fall back to legacy on-child fields until the migration runs
  const insProvider = insurance?.provider  || child.insurance_provider;
  const insMemberId = insurance?.memberId  || child.insurance_member_id;
  const name     = child.preferred_name || child.firstName || child.full_legal_name;
  const dob      = child.date_of_birth  || child.dob;
  const age      = dob ? Math.floor((Date.now() - new Date(dob)) / 31557600000) : null;
  const allergies = child.allergy_list   || (child.allergies ? [child.allergies] : []);
  const conditions = child.medical_conditions || (child.medNotes ? [child.medNotes] : []);
  const meds     = child.current_medications || (child.medications ? [{ name: child.medications }] : []);
  const hasCritical = (allergies.length > 0 && allergies[0] !== 'None') ||
                      (conditions.length > 0 && conditions[0] !== 'None') ||
                      child.epipen_on_premises;

  return (
    <div className="hiv-kid">
      {/* Identity */}
      <div className="hiv-kid__header">
        <div>
          <span className="hiv-kid__name">{name}</span>
          {child.full_legal_name && child.full_legal_name !== name && (
            <span className="hiv-kid__legal"> ({child.full_legal_name})</span>
          )}
        </div>
        <div className="hiv-kid__meta">
          {age !== null && <span>{age} yrs</span>}
          {child.blood_type && child.blood_type !== 'unknown' && (
            <span className="hiv-badge hiv-badge--red">Blood type: {child.blood_type}</span>
          )}
        </div>
      </div>

      {/* Critical medical — top */}
      {hasCritical && (
        <div className="hiv-critical-block">
          {allergies.filter(a => a && a !== 'None').map((a, i) => {
            const sev = child.allergy_severity?.[i];
            return (
              <div key={i} className={`hiv-allergy-row ${sev === 'severe' ? 'hiv-allergy-row--severe' : ''}`}>
                <span className="hiv-allergy-row__icon">{sev === 'severe' ? '🔥' : '⚠️'}</span>
                <span className="hiv-allergy-row__name">{a}</span>
                {sev && <span className="hiv-badge hiv-badge--amber">{sev}</span>}
              </div>
            );
          })}
          {child.epipen_on_premises && (
            <InfoRow icon="💉" label="EpiPen location" value={child.epipen_location || 'Ask parents'} urgent />
          )}
        </div>
      )}

      {/* Medical conditions */}
      {conditions.filter(c => c && c !== 'None').length > 0 && (
        <InfoRow icon="🌿" label="Medical conditions" value={conditions.filter(c => c !== 'None').join(', ')} />
      )}

      {/* Medications */}
      {meds.length > 0 && (
        <div className="hiv-med-list">
          <div className="hiv-label">💊 Medications</div>
          {meds.map((m, i) => (
            <div key={i} className="hiv-med-row">
              <span className="hiv-med-row__name">{m.name || m}</span>
              {m.dose      && <span className="hiv-med-row__detail">{m.dose}</span>}
              {m.frequency && <span className="hiv-med-row__detail">{m.frequency}</span>}
              {m.time_of_day && <span className="hiv-med-row__detail">@ {m.time_of_day}</span>}
              {m.storage_location && <span className="hiv-med-row__detail">📍 {m.storage_location}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Doctor */}
      {(child.physician_name || child.physician_phone) && (
        <div className="hiv-group">
          <div className="hiv-group__title">Doctor</div>
          {child.physician_name && <InfoRow icon="🌿" label="Physician" value={child.physician_name} />}
          {child.physician_phone && (
            <div className="hiv-phone-row">
              <span>After-hours line</span>
              <a href={`tel:${child.physician_phone}`} className="hiv-call-btn">{child.physician_phone}</a>
            </div>
          )}
          {child.after_hours_nurse_line && (
            <div className="hiv-phone-row">
              <span>Nurse line</span>
              <a href={`tel:${child.after_hours_nurse_line}`} className="hiv-call-btn">{child.after_hours_nurse_line}</a>
            </div>
          )}
          {!hideInsurance && insProvider && (
            <InfoRow icon="📋" label="Insurance" value={`${insProvider}${insMemberId ? ' · ' + insMemberId : ''}`} />
          )}
          {child.insurance_card_location && (
            <InfoRow icon="📍" label="Insurance card" value={child.insurance_card_location} />
          )}
        </div>
      )}

      {/* Routine */}
      {(child.bedtime || child.bedtime_routine_notes || child.dietary_restrictions || child.screen_time_rules || child.behavioral_notes) && (
        <div className="hiv-group">
          <div className="hiv-group__title">Routine &amp; behaviour</div>
          {child.bedtime && <InfoRow icon="🌙" label="Bedtime" value={child.bedtime} />}
          {child.bedtime_routine_notes && <InfoRow icon="📋" label="Bedtime routine" value={child.bedtime_routine_notes} />}
          {child.dietary_restrictions  && <InfoRow icon="🍃" label="Diet" value={child.dietary_restrictions} />}
          {child.screen_time_rules     && <InfoRow icon="📱" label="Screen time" value={child.screen_time_rules} />}
          {child.behavioral_notes      && <InfoRow icon="🌿" label="Behaviour notes" value={child.behavioral_notes} />}
        </div>
      )}
    </div>
  );
}

// ─── PETS SECTION ─────────────────────────────────────────────────────────────

function PetsSection({ pets }) {
  const [open, setOpen] = useState(pets[0]?.pet_id || null);
  if (!pets.length) return <p className="hiv-empty">No pets added yet.</p>;

  return (
    <div className="hiv-section">
      {pets.length > 1 && (
        <div className="hiv-kid-tabs">
          {pets.map(p => (
            <button key={p.pet_id}
              className={`hiv-kid-tab ${open === p.pet_id ? 'hiv-kid-tab--active' : ''}`}
              onClick={() => setOpen(p.pet_id)}>
              {p.name}
            </button>
          ))}
        </div>
      )}
      {pets.filter(p => p.pet_id === open || pets.length === 1).map(pet => (
        <PetDetail key={pet.pet_id} pet={pet} />
      ))}
    </div>
  );
}

function PetDetail({ pet }) {
  const meds = pet.current_medications || [];
  return (
    <div className="hiv-kid">
      <div className="hiv-kid__header">
        <div>
          <span className="hiv-kid__name">{pet.name}</span>
          <span className="hiv-kid__legal"> · {pet.species}{pet.breed ? ` (${pet.breed})` : ''}</span>
        </div>
        <div className="hiv-kid__meta">
          {pet.sex && <span>{pet.sex}</span>}
          {pet.weight_lbs && <span>{pet.weight_lbs} lbs</span>}
        </div>
      </div>

      {/* Critical */}
      {pet.known_allergies?.filter(a => a && a !== 'None').length > 0 && (
        <div className="hiv-critical-block">
          {pet.known_allergies.filter(a => a !== 'None').map((a, i) => (
            <div key={i} className="hiv-allergy-row">
              <span className="hiv-allergy-row__icon">⚠️</span>
              <span className="hiv-allergy-row__name">{a}</span>
            </div>
          ))}
        </div>
      )}
      {pet.medical_conditions?.filter(c => c).length > 0 && (
        <InfoRow icon="🌿" label="Medical" value={pet.medical_conditions.join(', ')} />
      )}
      {meds.length > 0 && (
        <div className="hiv-med-list">
          <div className="hiv-label">💊 Medications</div>
          {meds.map((m, i) => (
            <div key={i} className="hiv-med-row">
              <span className="hiv-med-row__name">{m.name || m}</span>
              {m.dose && <span className="hiv-med-row__detail">{m.dose}</span>}
              {m.frequency && <span className="hiv-med-row__detail">{m.frequency}</span>}
              {m.storage_location && <span className="hiv-med-row__detail">📍 {m.storage_location}</span>}
            </div>
          ))}
        </div>
      )}
      {pet.microchip_id && <InfoRow icon="📡" label="Microchip" value={pet.microchip_id} />}

      {/* Care */}
      {(pet.feeding_schedule || pet.food_brand_and_type || pet.outdoor_access_rules || pet.furniture_rules || pet.bedtime_crate_routine || pet.behavioral_notes) && (
        <div className="hiv-group">
          <div className="hiv-group__title">Daily care</div>
          {pet.feeding_schedule      && <InfoRow icon="🌱" label="Feeding" value={pet.feeding_schedule} />}
          {pet.food_brand_and_type   && <InfoRow icon="📍" label="Food" value={pet.food_brand_and_type} />}
          {pet.leash_crate_location  && <InfoRow icon="📍" label="Leash / crate" value={pet.leash_crate_location} />}
          {pet.outdoor_access_rules  && <InfoRow icon="🌿" label="Outdoor rules" value={pet.outdoor_access_rules} />}
          {pet.furniture_rules       && <InfoRow icon="🪵" label="Furniture rules" value={pet.furniture_rules} />}
          {pet.bedtime_crate_routine && <InfoRow icon="🌙" label="Bedtime" value={pet.bedtime_crate_routine} />}
          {pet.behavioral_notes      && <InfoRow icon="📋" label="Behaviour" value={pet.behavioral_notes} />}
        </div>
      )}

      {/* Vet */}
      {(pet.primary_vet_name || pet.emergency_vet_name) && (
        <div className="hiv-group">
          <div className="hiv-group__title">Vet contacts</div>
          {pet.primary_vet_name && (
            <div className="hiv-contact-card">
              <div className="hiv-contact-card__header">
                <span className="hiv-contact-card__name">{pet.primary_vet_name}</span>
                <span className="hiv-contact-card__role">Primary vet</span>
              </div>
              {pet.primary_vet_address && <div className="hiv-contact-card__dest">{pet.primary_vet_address}</div>}
              <div className="hiv-contact-card__phones">
                {pet.primary_vet_phone && <a href={`tel:${pet.primary_vet_phone}`} className="hiv-call-btn hiv-call-btn--forest">{pet.primary_vet_phone}</a>}
              </div>
            </div>
          )}
          {pet.emergency_vet_name && (
            <div className="hiv-contact-card hiv-contact-card--priority">
              <div className="hiv-contact-card__header">
                <span className="hiv-contact-card__name">{pet.emergency_vet_name}</span>
                <span className="hiv-contact-card__role">Emergency vet 24/7</span>
              </div>
              {pet.emergency_vet_address && <div className="hiv-contact-card__dest">{pet.emergency_vet_address}</div>}
              <div className="hiv-contact-card__phones">
                {pet.emergency_vet_phone && <a href={`tel:${pet.emergency_vet_phone}`} className="hiv-call-btn hiv-call-btn--red">{pet.emergency_vet_phone}</a>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── HOUSE SECTION ────────────────────────────────────────────────────────────

function HouseSection({ safety, location, norms = {} }) {
  const routines = norms.routines || {};
  const hasNorms = (norms.house_rules?.length > 0) || routines.morning || routines.after_school ||
                   routines.evening || norms.screen_time_defaults || norms.visitor_norms;
  return (
    <div className="hiv-section">
      {/* Norms & routines — the "how our house runs" block caregivers need most */}
      {hasNorms && (
        <div className="hiv-group">
          <div className="hiv-group__title">Norms &amp; routines</div>
          {routines.morning       && <InfoRow icon="🌅" label="Morning" value={routines.morning} />}
          {routines.after_school  && <InfoRow icon="🎒" label="After school" value={routines.after_school} />}
          {routines.evening       && <InfoRow icon="🌙" label="Evening" value={routines.evening} />}
          {norms.screen_time_defaults && <InfoRow icon="📱" label="Screen time" value={norms.screen_time_defaults} />}
          {norms.visitor_norms    && <InfoRow icon="🚪" label="Visitors" value={norms.visitor_norms} />}
          {norms.house_rules?.length > 0 && (
            <InfoRow icon="📋" label="House rules" value={norms.house_rules.join(' · ')} />
          )}
        </div>
      )}
      {/* Access */}
      {(safety.wifi_ssid || safety.alarm_code || safety.garage_gate_code || safety.spare_key_location) && (
        <div className="hiv-group">
          <div className="hiv-group__title">Access</div>
          {safety.wifi_ssid      && <InfoRow icon="🌬️" label="WiFi network" value={safety.wifi_ssid} />}
          {safety.wifi_password  && <InfoRow icon="🌬️" label="WiFi password" value={safety.wifi_password} />}
          {safety.alarm_code     && <InfoRow icon="🔐" label="Alarm code" value={safety.alarm_code} />}
          {safety.alarm_company_name && (
            <div className="hiv-phone-row">
              <span>{safety.alarm_company_name}</span>
              {safety.alarm_company_phone && <a href={`tel:${safety.alarm_company_phone}`} className="hiv-call-btn">{safety.alarm_company_phone}</a>}
            </div>
          )}
          {safety.garage_gate_code   && <InfoRow icon="🪵" label="Garage / gate code" value={safety.garage_gate_code} />}
          {safety.spare_key_location && <InfoRow icon="🌾" label="Spare key" value={safety.spare_key_location} />}
        </div>
      )}

      {/* Safety items */}
      {(safety.first_aid_kit_location || safety.fire_extinguisher_location || safety.aed_location || safety.circuit_breaker_location || safety.gas_shutoff_location) && (
        <div className="hiv-group">
          <div className="hiv-group__title">Safety equipment</div>
          {safety.first_aid_kit_location   && <InfoRow icon="🌿" label="First aid kit" value={safety.first_aid_kit_location} />}
          {safety.fire_extinguisher_location && <InfoRow icon="🔥" label="Fire extinguisher" value={safety.fire_extinguisher_location} />}
          {safety.aed_location             && <InfoRow icon="💉" label="AED / defibrillator" value={safety.aed_location} />}
          {safety.circuit_breaker_location  && <InfoRow icon="🪨" label="Circuit breaker" value={safety.circuit_breaker_location} />}
          {safety.gas_shutoff_location      && <InfoRow icon="🌬️" label="Gas shutoff" value={safety.gas_shutoff_location} />}
        </div>
      )}

      {/* Rules */}
      {(safety.off_limits_areas?.length > 0 || safety.no_visitor_policy !== undefined || safety.visitor_policy_notes) && (
        <div className="hiv-group">
          <div className="hiv-group__title">House rules</div>
          {safety.off_limits_areas?.length > 0 && (
            <InfoRow icon="🚫" label="Off-limits areas" value={safety.off_limits_areas.join(', ')} />
          )}
          {safety.no_visitor_policy === true && (
            <InfoRow icon="🚫" label="Visitors" value="No visitors permitted" />
          )}
          {safety.visitor_policy_notes && (
            <InfoRow icon="📋" label="Visitor notes" value={safety.visitor_policy_notes} />
          )}
        </div>
      )}

      {!safety.wifi_ssid && !safety.alarm_code && !safety.first_aid_kit_location && (
        <p className="hiv-empty">No house info added yet.</p>
      )}
    </div>
  );
}

// ─── NOTES SECTION ────────────────────────────────────────────────────────────

function NotesSection({ notes }) {
  if (!notes) return <p className="hiv-empty">No general notes.</p>;
  return (
    <div className="hiv-section">
      <p style={{ fontSize: 14, color: 'var(--forest-dark)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {notes}
      </p>
    </div>
  );
}

// ─── SHARED SUB-COMPONENTS ────────────────────────────────────────────────────

function InfoRow({ icon, label, value, urgent }) {
  return (
    <div className={`hiv-info-row ${urgent ? 'hiv-info-row--urgent' : ''}`}>
      <span className="hiv-info-row__icon">{icon}</span>
      <div>
        <span className="hiv-info-row__label">{label}</span>
        <span className="hiv-info-row__value">{value}</span>
      </div>
    </div>
  );
}
